/**
 * Helpers de reconexão da sala (jitter, grace do SDK, saída forçada).
 */

/** Tentativas nativas do socket antes de interferir com soft reconnect. */
export const SDK_SOCKET_RECONNECT_GRACE_ATTEMPTS = 10;

/** Enquanto a aba está escondida, não forçar leaveRoom/rejoin (mata sessão cedo). */
export const HIDDEN_TAB_RECONNECT_DELAY_MS = 45_000;

/** Grace após socket disconnected — dar tempo ao SDK reconectar sozinho. */
export const SOCKET_DISCONNECTED_GRACE_MS_MOBILE = 22_000;
export const SOCKET_DISCONNECTED_GRACE_MS_DESKTOP = 50_000;

/** Grace quando socket está em reconnecting nativo do SDK. */
export const SOCKET_RECONNECTING_GRACE_MS_MOBILE = 32_000;
export const SOCKET_RECONNECTING_GRACE_MS_DESKTOP = 65_000;

/**
 * roomLeft com ICE/PC ainda connected: a mídia está ok; esperamos mais antes
 * de leaveRoom+rejoin para não matar sessão por oscilação de signaling.
 */
export const ROOM_LEFT_WITH_MEDIA_CONNECTED_DELAY_MS = 22_000;
export const ROOM_LEFT_WITH_MEDIA_CONNECTED_DELAY_MS_DESKTOP = 90_000;

/** Reavalia se a mídia caiu antes de forçar soft reconnect (signaling-only). */
export const PASSIVE_RECOVERY_CHECK_MS_DESKTOP = 120_000;
export const PASSIVE_RECOVERY_CHECK_MS_MOBILE = 90_000;

export function isMediaTransportHealthy(
    snap: {
        iceConnectionState?: string | null;
        connectionState?: string | null;
    } | null | undefined,
): boolean {
    if (!snap) {
        return false;
    }
    const ice = snap.iceConnectionState;
    const pc = snap.connectionState;
    return (
        ice === "connected" ||
        ice === "completed" ||
        pc === "connected"
    );
}

/**
 * Signaling caiu mas WebRTC segue ok e a reunião existe no Firestore — não
 * fazer leaveRoom+rejoin (causa raiz das quedas a cada ~1–2 min no RTK).
 */
export function shouldPreserveSessionDespiteSignalingLoss(
    mediaStillConnected: boolean,
    meetingDocExists: boolean,
): boolean {
    return mediaStillConnected && meetingDocExists;
}

/** Jitter determinístico por usuário para espalhar soft reconnects (0..maxMs). */
export function reconnectJitterMs(userId: string | undefined | null, maxMs = 3000): number {
    if (!maxMs || maxMs <= 0) {
        return 0;
    }
    if (!userId) {
        return Math.floor(Math.random() * maxMs);
    }
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        hash = (hash * 31 + userId.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % maxMs;
}

/**
 * Anti-stampede em quedas gerais (roomLeft=ended para todos):
 * moderador/dono reconecta primeiro; participantes esperam offset + jitter maior.
 * Não altera keep-alive / segundo plano — só espalha o soft reconnect.
 */
export const STAMPEDE_JITTER_MS_MODERATOR = 2_000;
export const STAMPEDE_JITTER_MS_PARTICIPANT = 15_000;
/** Participantes esperam o moderador estabilizar antes de pedir token novo. */
export const STAMPEDE_PARTICIPANT_BASE_OFFSET_MS = 8_000;

export function reconnectStampedeExtraMs(options: {
    userId?: string | null;
    isModerator: boolean;
}): number {
    const { userId, isModerator } = options;
    if (isModerator) {
        return reconnectJitterMs(userId, STAMPEDE_JITTER_MS_MODERATOR);
    }
    return (
        STAMPEDE_PARTICIPANT_BASE_OFFSET_MS +
        reconnectJitterMs(userId, STAMPEDE_JITTER_MS_PARTICIPANT)
    );
}

/**
 * Só expulsa imediatamente em kick/reject ou ended quando a reunião já não existe
 * no Firestore. Se a mídia WebRTC ainda está connected, não expulsa por ended
 * ausente — o doc pode ter sido removido prematuramente (monitor/RTK ENDED
 * transitório) enquanto o terapeuta ainda está na chamada.
 */
export function shouldForceExitOnRoomLeft(
    state: string | undefined | null,
    meetingDocExists: boolean,
    mediaStillConnected = false,
): boolean {
    if (!state) {
        return false;
    }
    if (state === "kicked" || state === "rejected") {
        return true;
    }
    if (state === "ended") {
        if (!meetingDocExists && mediaStillConnected) {
            return false;
        }
        return !meetingDocExists;
    }
    return false;
}

export function isDocumentHidden(): boolean {
    return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/** Em segundo plano, alonga o delay para não derrubar a sessão com rejoin agressivo. */
export function reconnectDelayForVisibility(requestedMs: number): number {
    if (isDocumentHidden()) {
        return Math.max(requestedMs, HIDDEN_TAB_RECONNECT_DELAY_MS);
    }
    return requestedMs;
}
